import { applyToSearchParams, writeCsv } from "@/utils/search-params";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ACTIVE_VALUES,
  DEFAULT_PAGE,
  SECTOR_EMAILS_PARAMS,
  SECTORS,
  SHIPPING_TYPE_VALUES,
} from "../../constants";
import {
  AppliedSectorEmailsFilters,
  SectorEmailsPagination,
  SectorEmailsSorting,
} from "../types";

export interface SectorEmailsFilterActions {
  setSearch: (search: string) => void;
  setPagination: (pagination: SectorEmailsPagination) => void;
  setSorting: (sorting: SectorEmailsSorting) => void;
  applyFilters: (filters: AppliedSectorEmailsFilters) => void;
  resetFilters: () => void;
}

const writeSubset = <TValue extends string | boolean>(
  selected: readonly TValue[],
  allowed: readonly TValue[],
): string | null => {
  if (selected.length === allowed.length) return null;

  return writeCsv(selected.map(String));
};

export const useSectorEmailsFilterActions = (): SectorEmailsFilterActions => {
  const [searchParams, setSearchParams] = useSearchParams();

  const update = useCallback(
    (updates: Readonly<Record<string, string | null>>): void => {
      setSearchParams(applyToSearchParams(searchParams, updates));
    },
    [searchParams, setSearchParams],
  );

  return {
    setSearch: (search: string): void => {
      update({
        [SECTOR_EMAILS_PARAMS.SEARCH]: search,
        [SECTOR_EMAILS_PARAMS.PAGE]: null,
      });
    },
    setPagination: (pagination: SectorEmailsPagination): void => {
      update({
        [SECTOR_EMAILS_PARAMS.PAGE]: String(pagination.page),
        [SECTOR_EMAILS_PARAMS.PER_PAGE]: String(pagination.perPage),
      });
    },
    setSorting: (sorting: SectorEmailsSorting): void => {
      update({
        [SECTOR_EMAILS_PARAMS.SORT]: sorting.sort ?? null,
        [SECTOR_EMAILS_PARAMS.DIRECTION]: sorting.direction ?? null,
        [SECTOR_EMAILS_PARAMS.PAGE]: null,
      });
    },
    applyFilters: (filters: AppliedSectorEmailsFilters): void => {
      update({
        [SECTOR_EMAILS_PARAMS.ACTIVE]: writeSubset(
          filters.active,
          ACTIVE_VALUES,
        ),
        [SECTOR_EMAILS_PARAMS.SECTORS]: writeSubset(filters.sectors, SECTORS),
        [SECTOR_EMAILS_PARAMS.SHIPPING_TYPES]: writeSubset(
          filters.shipping_types,
          SHIPPING_TYPE_VALUES,
        ),
        [SECTOR_EMAILS_PARAMS.PAGE]: null,
      });
    },
    resetFilters: (): void => {
      update({
        [SECTOR_EMAILS_PARAMS.ACTIVE]: null,
        [SECTOR_EMAILS_PARAMS.SECTORS]: null,
        [SECTOR_EMAILS_PARAMS.SHIPPING_TYPES]: null,
        [SECTOR_EMAILS_PARAMS.PAGE]: String(DEFAULT_PAGE),
      });
    },
  };
};
